import { consoleLoggingIntegration, getIsolationScope, init, isEnabled, setAttributes, } from '@sentry/browser';
import { allowsPerformanceSync, performanceCookieConsentAllowedOnce } from './consent.service';
const VIEW_TAG = 'view';
const VIEW_ASSOCIATE_VALUE = 'associate';
/** Third-party script URLs excluded from error reporting. */
const THIRD_PARTY_DENY_URLS = [
    /elevar/i,
    /getelevar/i,
    /visually/i,
    /vsly\//i,
    /swym/i,
    /bazaarvoice/i,
    // /shopifycloud\/storefront/i, // reevaluate later if we want to block
    /cdn\.shopify\.com\/wpm/i,
    /packs\/js\/shop_now/i,
    /widget/i,
    /uxa\//i,
    /shop_events_listener/i,
    /consent-tracking-api/i,
    /monorail-edge\.shopifysvc\.com/i,
    /boost-pfs-filter/i,
    /awin-shopify-integration/i,
    /webmcp/i,
    /shopping-behaviour/i,
    // Shopify Shop JS, storefront loaders, and web pixel sandbox workers.
    /shopifycloud\/shop-js/i,
    /shopifycloud\/storefront\/assets\/storefront\/load_feature/i,
    /web-pixels@/i,
    /\/web-pixels\//i,
    /\/cdn\/wpm\//i,
];
/** Known-noise error messages from vendors, browsers, and network conditions. */
const IGNORED_ERROR_PATTERNS = [
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications',
    'Load failed',
    'AbortError',
    'HTTP Client Error',
    'Importing a module script failed',
    'Failed to fetch dynamically imported module',
    'error loading dynamically imported module',
    'NetworkError when attempting to fetch resource',
    'Non-Error promise rejection captured',
    /webkit\.messageHandlers/,
    /sendDataToNative/,
    /Java object is gone/,
    /Elevar:/,
    /^swym:/,
    /registerTool.*ModelContext/,
    /could not load https:\/\/apps\.bazaarvoice\.com/,
    /Failed to load https:\/\/assets\.visually\.io/,
    // Shopify Web Pixels Manager telemetry (UNTUCKIT-SHOPIFY-THEME-2V).
    'Failed to fetch (otlp-http-production.shopifysvc.com)',
    // Shopify consent-tracking-api script load failures (UNTUCKIT-SHOPIFY-THEME-1J).
    /load error: https:\/\/cdn\.shopify\.com\/shopifycloud\/consent-tracking-api\//,
    /load error: https:\/\/[^/]+\/cdn\/shopifycloud\/consent-tracking-api\//,
    // Shopify Shop JS dynamic module load failures (UNTUCKIT-SHOPIFY-THEME-12, -3W).
    /load error: https:\/\/cdn\.shopify\.com\/shopifycloud\/shop-js\//,
    // Shopify web pixel / WPM worker script load failures (UNTUCKIT-SHOPIFY-THEME-5J, -78, -8N).
    /importScripts on 'WorkerGlobalScope'.*\/web-pixels/i,
    /importScripts on 'WorkerGlobalScope'.*\/cdn\/wpm\//i,
    // Third-party app inline scripts not managed in this repo (UNTUCKIT-SHOPIFY-THEME-2W, -7N).
    /extractFilteredSchemaValuesFromJSONLD/,
    /script_src_app_theme_embed/,
];
const DEFAULT_CONSOLE_LOGGING_LEVELS = ['error'];
function isLocalHost() {
    const hostname = window.location.hostname;
    return hostname === 'localhost' || hostname === '127.0.0.1';
}
function isAssociateView() {
    return new URLSearchParams(window.location.search).get('view') === 'associate';
}
function isSentryDebugEnabled() {
    return new URLSearchParams(window.location.search).has('sentry_debug');
}
function parseConsoleLoggingLevels(levels) {
    if (!levels?.trim()) {
        return DEFAULT_CONSOLE_LOGGING_LEVELS;
    }
    return levels.split(',').map((level) => level.trim()).filter(Boolean);
}
function beforeSend(event) {
    const frames = event.exception?.values?.[0]?.stacktrace?.frames;
    if (!frames?.length) {
        return event;
    }
    const hasInAppFrame = frames.some((frame) => frame.in_app === true);
    return hasInAppFrame ? event : null;
}
function buildBaseOptions() {
    const sentryConfig = window.UNTUCKitConfig?.sentry;
    return {
        dsn: sentryConfig?.dsn,
        environment: sentryConfig?.environment,
        ignoreErrors: IGNORED_ERROR_PATTERNS,
        denyUrls: THIRD_PARTY_DENY_URLS,
        beforeSend,
        debug: isSentryDebugEnabled(),
        integrations: [
            consoleLoggingIntegration({
                levels: parseConsoleLoggingLevels(sentryConfig?.consoleLoggingLevels),
            }),
        ],
        enableLogs: true,
    };
}
function applyAssociateViewContext() {
    if (!isAssociateView()) {
        return;
    }
    getIsolationScope().setTag(VIEW_TAG, VIEW_ASSOCIATE_VALUE);
    setAttributes({
        [VIEW_TAG]: VIEW_ASSOCIATE_VALUE,
    });
}
function initSentry() {
    if (isEnabled()) {
        return;
    }
    init(buildBaseOptions());
    applyAssociateViewContext();
}
/**
 * Initializes Sentry with console logging instrumentation.
 * Associate views (`?view=associate`) skip consent; all other traffic waits for
 * PieEye Performance consent (pieeye-category-performance).
 */
export function setupSentry() {
    const sentryConfig = window.UNTUCKitConfig?.sentry;
    if (!sentryConfig?.enable || isLocalHost()) {
        return;
    }
    if (isAssociateView()) {
        initSentry();
        return;
    }
    if (allowsPerformanceSync()) {
        initSentry();
        return;
    }
    performanceCookieConsentAllowedOnce.subscribe(() => {
        initSentry();
    });
}
